import type { FC } from "react";
import { memo } from "react";

import PageLayout from "@/src/shared/page-layout";
import { defaultToTrue } from "@Components/Modules/Helpers";
import { OffersIndex } from "@Routes/OffersLinks";
import type { QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

import type { IComponentViewStateEntryPoint, IEntryPointViewState } from "../common/ComponentViewState";
import type { ILatestOffers, ILatestOffersViewState } from "../common/LatestOffers";
import { LatestOffers } from "../common/LatestOffers";
import type { ILocationInfo } from "../location/LocationInfo";
import { LocationInfo } from "../location/LocationInfo";
import type { ISpecialMoments } from "../moment/SpecialMoments";
import SpecialMoments from "../moment/SpecialMoments";

import * as cssPortal from "../shared/Portal.module.css";

export interface IOffers extends IComponentViewStateEntryPoint<IOffersViewState> {
    specialMoments: ISpecialMoments;
    latestOffers: ILatestOffers;
    locationInfo: ILocationInfo;
}

export interface IOffersViewState extends IEntryPointViewState {
    latestOffersViewState: ILatestOffersViewState;
}

export const Offers: FC<IOffers> = memo((props) => {
    const updateViewState = (latestOffersViewState: ILatestOffersViewState): boolean =>
        (props.updateViewState &&
            props.updateViewState<IOffersViewState>({
                latestOffersViewState,
                preventScroll: true,
            })) ||
        true;

    return (
        <PageLayout layoutAdUnitPath="okazje" slugTarget={OffersIndex} breadcrumbs={[{ labelKey: "offers" }]}>
            <div {...stylex.props(styles.wrapper)}>
                <SpecialMoments {...props.specialMoments} />
                <div className={cssPortal.standardContainer}>
                    <LocationInfo {...stylex.props(styles.location)} {...props.locationInfo} />
                    <LatestOffers
                        {...props.latestOffers}
                        updateViewState={updateViewState}
                        componentState={props.componentState && props.componentState.latestOffersViewState}
                        panelTitleElement="h2"
                    />
                </div>
            </div>
        </PageLayout>
    );
}, defaultToTrue);

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    wrapper: {
        paddingBottom: {
            default: 100,
            [xsMax]: 50,
        },
    },
    location: {
        strokeWidth: 2,
        paddingTop: { default: 50, [xsMax]: 20 },
    },
});
